<template>
  <div id="myJourney">
    <el-card
      shadow="hover"
      class="mainCard clear-card defaultCursor topJourney"
      :body-style="{
        padding: '20px 30px 35px 30px',
      }"
    >
      <div class="topTitle">
        {{ $t('aside.myJourney') }}
      </div>

      <div class="topContent">
        {{ $t('myJourney.description') }}
      </div>
    </el-card>

    <div
      v-for="(session, key) in formattedSessions"
      :key="key"
      class="journeyWrap"
    >
      <Tasks
        :session-id="session.session_id"
        :modal-type.sync="modalType"
        :modal-visible.sync="modalVisible"
      />

      <el-card
        shadow="hover"
        class="mainCard clear-card defaultCursor"
        :body-style="{
          padding: '20px 0',
          position: 'relative'
        }"
      >
        <div class="header">
          <div class="titleWrap">
            <span class="title">{{ session.session_id | sessionToTitle }} - </span>
            <span v-if="session.slogan">{{ $t(`myJourney.slogan.${session.slogan}`) }}</span>
            <span
              v-if="['desktop', 'laptop'].includes($mq)"
              class="duration"
            >
              ({{ session.duration }} {{ $t('common.minutes') }})
            </span>
          </div>

          <div
            class="value"
            :style="{'font-size': !session.meeting_time ? fontSize.none : fontSize.date}"
            :class="{noData: !session.meeting_time}"
          >
            <span
              v-if="session.meeting_time"
              class="meetingPrefix"
            >
              {{ $t('myJourney.meetingDate') }}
            </span>
            <span class="meetingTime">
              {{ session.meeting_time ? session.meeting_time : `${$t('myJourney.toBeScheduled')}` }}
            </span>
          </div>
        </div>

        <div class="content">
          <div
            v-if="session.detail"
            class="detail"
          >
            {{ session.detail }}
          </div>
        </div>
      </el-card>
    </div>

    <div class="mainCard journeyWrap closingText">
      <em>
        {{ $t('myJourney.footer') }}
      </em>
    </div>

    <transition
      name="customTransition"
      mode="out-in"
    >
      <ModalDocument
        v-if="modalVisible"
        :show.sync="modalVisible"
        :type="modalType"
      />
    </transition>
  </div>
</template>

<script>
import { mapState } from 'vuex';
import api from '@/api';
import Tasks from './Tasks.vue';
import ModalDocument from './ModalDocument.vue';
// import sessionsData from './data/myJourneyData';

export default {
  components: {
    Tasks,
    ModalDocument,
  },

  filters: {
    sessionToTitle(value) {
      if (value === 'on_boarding') return 'Coach Selection';
      return `Session ${value}`;
    },
  },

  data() {
    return {
      // sessions: sessionsData,
      sessions: null,
      meetings: [],
      modalType: '',
      modalVisible: false,
    };
  },

  computed: {
    ...mapState({
      currentSessionId: (state) => state.processStatus.process.session_id,
      LeaderTimeZone: (state) => state.user.user.time_zone,
      status: (state) => state.processStatus.process,
    }),

    platformName() {
      let platform = 'gcg_platform';
      if (process.env.VUE_APP_SERVICE) { platform = 'ing_gcg_platform'; }
      return platform;
    },

    formattedSessions() {
      let sessions = this.sessions.map((el) => {
        const session = { ...el };
        session.detail = this
          .$t(`myJourney.detail.${this.platformName}.no_${session.session_id}`);
        const correspondingMeeting = this.meetings.find(
          (met) => met.session_id === `session_${session.session_id}`,
        );
        if (correspondingMeeting && correspondingMeeting.start_time) {
          session.meeting_time = this.$moment(correspondingMeeting.start_time).format('YYYY-MM-DD HH:mm');
          session.meeting_status = correspondingMeeting.status;
        }
        return session;
      });

      // Cut off MyJourney by session duration
      if (this.status && this.status.duration
        && this.status.duration !== 13) {
        const offset = -(13 - (this.status.duration));
        sessions = sessions.slice(0, offset);
      } else if (this.status.duration === 13) {
        return sessions;
      } else {
        const offset = -(13 - (4));
        sessions = sessions.slice(0, offset);
      }

      return sessions;
    },

    fontSize() {
      const font = {};
      if (this.$mq === 'mobile') {
        font.title = '16px';
        font.date = '14px';
        font.none = '14px';
      } else if (this.$mq === 'tablet') {
        font.title = '16px';
        font.date = '15px';
        font.none = '14px';
      } else {
        font.title = '16px';
        font.date = '16px';
        font.none = '14px';
      }
      return font;
    },
  },

  created() {
    this.getMeetings();
    this.loadMyJourneyJSON();
  },

  mounted() {
    if (this.$route.query.key) {
      this.openModal(this.$route.query.key);
    }
  },

  methods: {
    loadMyJourneyJSON() {
      if (process.env.VUE_APP_MY_JOURNEY_DATA) {
        // eslint-disable-next-line global-require, import/no-dynamic-require
        this.sessions = require(process.env.VUE_APP_MY_JOURNEY_DATA).default;
      } else {
        // eslint-disable-next-line global-require
        this.sessions = require('./data/myJourneyData').default;
      }
    },

    handleCTA(task, session) {
      if (!this.isPast(session)) return;
      const actionFormat = task.cta.split(':');
      if (actionFormat[0] === 'route') {
        this.$router.push({ name: actionFormat[1] }).catch((err) => {});
      }
    },

    openModal(key) {
      if (key) {
        this.modalType = key;
        this.modalVisible = true;
      }
    },

    isPast(sessionId) {
      if (sessionId === 'on_boarding') return true;
      const leaderSessionId = this.currentSessionId.split('_')[1];
      if (this.currentSessionId === 'on_boarding') {
        return sessionId === leaderSessionId;
      }
      if ((sessionId * 1) === (leaderSessionId * 1) + 1) {
        const currentSession = this.formattedSessions.find(
          (el) => el.session_id === leaderSessionId,
        );
        return currentSession.meeting_status === 'completed';
      }
      return sessionId * 1 <= leaderSessionId * 1;
    },

    async getMeetings() {
      try {
        const response = await api.getJourney();
        this.meetings = response.data;
      } catch (err) {
        console.log(err);
        if (err.msg) {
          this.$message.error(err.msg);
        }
      }
    },
  },
};
</script>

<style lang="scss" scoped>
#myJourney {
  .topJourney {
    .topTitle {
      font-size: 20px;
      font-weight: bold;
    }

    .topContent {
      margin-top: 15px;
    }

    .topButton {
      margin-top: 15px;

      &.clearBtn {
        background: transparent;
        color: white;
        border-width: 2px;
      }
    }
  }

  .closingText {
    margin-top: 20px;
    color: var(--themeFontColor);
    word-break: break-word;
  }

  .el-card {
    margin-bottom: 15px;

    .header {
      font-size: 18px;
      padding: 0 30px;
      display: flex;
      justify-content: space-between;
      align-items: center;

      .titleWrap {
        display: flex;
        flex-wrap: wrap;
        white-space: pre;

        .title {
          font-weight: bold;
        }

        .duration {
          font-style: italic;
        }
      }

      .value {
        display: flex;
        flex-wrap: wrap;
        justify-content: flex-end;
        font-size: 14px;

        .meetingTime {
          font-weight: bold;
          padding-left: 5px;
        }
      }
    }

    .content {
      padding-top: 5px !important;

      .detail {
        padding: 15px 30px;
      }
    }
  }
}
</style>
